import {
  CLIENT_STATUS_LABELS,
  CLIENT_STATUSES,
  countryName,
  EDUCATION_LEVEL_LABELS,
  MESSAGE_CHANNEL_LABELS,
  MESSAGE_CHANNELS,
  MESSAGE_REF_TYPE_LABELS,
  PASSPORT_STATUS_LABELS,
  manualSendSchema,
  type ClientStatus,
  type CreateClientInput,
  type EducationLevel,
  type ManualSendInput,
  type MessageChannel,
  type MessageRefType,
  type PassportStatus,
} from '@crm/shared'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, FileText, Bell, MessageSquare, Activity, Send, Trash2 } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Badge, Card, Skeleton, type BadgeVariant } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select, Textarea } from '@/components/ui/select'
import {
  clientActivityQuery,
  clientNotesQuery,
  clientQuery,
  useAddNote,
  useDeleteClient,
  useUpdateClient,
  type ClientDetail,
} from '@/features/clients/api'
import { ClientForm } from '@/features/clients/client-form'
import { jobCategoryOptions, statusLabel, statusTone } from '@/features/clients/options'
import {
  clientMessagesQuery,
  templatesQuery,
  useManualSend,
  type ClientMessage,
} from '@/features/messaging/api'
import {
  clientRemindersQuery,
  useCreateReminder,
  useMarkReminderDone,
  useDeleteReminder,
} from '@/features/reminders/api'
import { ApiClientError } from '@/lib/api'
import { cn, formatDateTime } from '@/lib/utils'
import { Route as AppRoute } from '../../_app'

export const Route = createFileRoute('/_app/clients/$clientId')({
  component: ClientProfilePage,
})

type Tab = 'details' | 'notes' | 'messages' | 'activity' | 'reminders'

function ClientProfilePage() {
  const { clientId } = Route.useParams()
  const { me } = AppRoute.useRouteContext()
  const navigate = useNavigate()

  const [tab, setTab] = useState<Tab>('details')
  const [editing, setEditing] = useState(false)

  const { data, isLoading, isError } = useQuery(clientQuery(clientId))
  const client = data?.client

  if (isLoading) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 p-8">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }
  if (isError || !client) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-medium">Client not found</p>
        <Link to="/clients" search={{}} className="mt-2 inline-block text-sm underline">
          Back to clients
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div>
        <Link
          to="/clients"
          search={{}}
          className="inline-flex items-center gap-1 text-sm text-[var(--color-muted-foreground)] hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to clients
        </Link>
      </div>

      <ProfileHeader
        client={client}
        canDelete={me.permissions.includes('clients:delete')}
        canEdit={me.permissions.includes('clients:update')}
        editing={editing}
        onEdit={() => setEditing(true)}
        onDeleted={() => navigate({ to: '/clients', search: {} })}
      />

      <div className="flex gap-1 border-b border-[var(--color-border)]">
        {(['details', 'notes', 'reminders', 'messages', 'activity'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              tab === t
                ? 'border-b-2 border-[var(--color-primary)] px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]',
            )}
          >
            {t[0]!.toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'details' &&
        (editing ? (
          <EditDetails client={client} onDone={() => setEditing(false)} />
        ) : (
          <ViewDetails client={client} />
        ))}
      {tab === 'notes' && (
        <NotesTab clientId={clientId} canWrite={me.permissions.includes('clients:update')} />
      )}
      {tab === 'reminders' && <RemindersTab clientId={clientId} />}
      {tab === 'messages' && (
        <MessagesTab client={client} canSend={me.permissions.includes('campaigns:send')} />
      )}
      {tab === 'activity' && <ActivityTab clientId={clientId} />}
    </div>
  )
}

function ProfileHeader({
  client,
  canDelete,
  canEdit,
  editing,
  onEdit,
  onDeleted,
}: {
  client: ClientDetail
  canDelete: boolean
  canEdit: boolean
  editing: boolean
  onEdit: () => void
  onDeleted: () => void
}) {
  const update = useUpdateClient(client.id)
  const remove = useDeleteClient()
  const [confirmDelete, setConfirmDelete] = useState(false)

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{client.fullName}</h1>
          <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
            {client.phone}
            {client.district && ` · ${client.district}`}
            {' · added '}
            {formatDateTime(client.createdAt)}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusTone(client.status)}>{statusLabel(client.status)}</Badge>

          {/* Status is changed here rather than buried in the edit form — it's
              the field staff touch most and shouldn't require entering edit mode. */}
          {canEdit && (
            <Select
              className="h-8 w-40"
              value={client.status}
              disabled={update.isPending}
              onChange={(e) => update.mutate({ status: e.target.value as ClientStatus })}
              aria-label="Change status"
            >
              {CLIENT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CLIENT_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          )}

          {canEdit && !editing && (
            <Button variant="outline" size="sm" onClick={onEdit}>
              Edit
            </Button>
          )}

          {canDelete && (
            <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <Card className="w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold">Delete {client.fullName}?</h2>
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
              They&rsquo;ll be hidden from lists and excluded from campaigns. An admin can restore
              them later.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                loading={remove.isPending}
                onClick={() => remove.mutate(client.id, { onSuccess: onDeleted })}
              >
                Delete
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Card>
  )
}

function ViewDetails({ client }: { client: ClientDetail }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <DetailCard title="Identity">
        <Row label="Name with initials" value={client.initialsName} />
        {/* Full NIC/passport are shown here, not in lists — and the read is audited. */}
        <Row label="NIC" value={client.nic} mono />
        <Row label="Date of birth" value={client.dob} />
        <Row label="Gender" value={client.gender} capitalise />
        <Row label="Marital status" value={client.maritalStatus} capitalise />
        <Row label="Nationality" value={client.nationality} />
        <Row label="Religion" value={client.religion} />
      </DetailCard>

      <DetailCard title="Contact">
        <Row label="Phone" value={client.phone} />
        <Row label="WhatsApp" value={client.whatsapp} />
        <Row label="Email" value={client.email} />
        <Row label="Address" value={client.address} />
        <Row label="City" value={client.city} />
        <Row label="District" value={client.district} />
        <Row label="Emergency contact" value={client.emergencyName} />
        <Row label="Emergency phone" value={client.emergencyPhone} />
      </DetailCard>

      <DetailCard title="Passport">
        <Row
          label="Status"
          value={PASSPORT_STATUS_LABELS[client.passportStatus as PassportStatus]}
        />
        <Row label="Number" value={client.passportNo} mono />
        <Row label="Issued" value={client.passportIssued} />
        <Row label="Expires" value={client.passportExpiry} />
      </DetailCard>

      <DetailCard title="Work">
        <Row
          label="Education"
          value={
            client.educationLevel
              ? EDUCATION_LEVEL_LABELS[client.educationLevel as EducationLevel]
              : null
          }
        />
        <Row
          label="Local experience"
          value={client.experienceYears ? `${client.experienceYears} years` : null}
        />
        <Row
          label="Job categories"
          value={
            client.jobCategoryIds.length
              ? client.jobCategoryIds
                  .map((id) => jobCategoryOptions.find((o) => o.value === id)?.label ?? id)
                  .join(', ')
              : null
          }
        />
        <Row
          label="Preferred countries"
          value={
            client.preferredCountries.length
              ? client.preferredCountries.map(countryName).join(', ')
              : null
          }
        />
        <Row
          label="Expected salary"
          value={
            client.expectedSalary
              ? `${client.expectedSalary} ${client.salaryCurrency ?? ''}`.trim()
              : null
          }
        />
        <Row label="Available from" value={client.availableFrom} />
        <Row
          label="Languages"
          value={
            client.languages.length
              ? client.languages.map((l) => `${l.language} (${l.proficiency})`).join(', ')
              : null
          }
        />
      </DetailCard>

      {client.foreignExperience.length > 0 && (
        <DetailCard title="Foreign experience">
          {client.foreignExperience.map((f) => (
            <Row
              key={f.id}
              label={countryName(f.countryCode)}
              value={`${f.role} — ${f.years} years`}
            />
          ))}
        </DetailCard>
      )}

      <DetailCard title="Agency">
        <Row label="Assigned to" value={client.assignedUserName ?? 'Unassigned'} />
        <Row
          label="Source"
          value={client.source === 'manual' ? 'Added by staff' : 'Self-registered'}
        />
        <Row
          label="Marketing consent"
          value={
            [
              client.consentSms && 'SMS',
              client.consentWhatsapp && 'WhatsApp',
              client.consentEmail && 'Email',
            ]
              .filter(Boolean)
              .join(', ') || 'None'
          }
        />
        <Row label="Last updated" value={formatDateTime(client.updatedAt)} />
      </DetailCard>
    </div>
  )
}

/** Maps the API's nulls back to the form's empty strings/undefined. */
function toFormValues(client: ClientDetail): Partial<CreateClientInput> {
  return {
    fullName: client.fullName,
    initialsName: client.initialsName ?? undefined,
    dob: client.dob ?? undefined,
    gender: (client.gender ?? undefined) as CreateClientInput['gender'],
    maritalStatus: (client.maritalStatus ?? undefined) as CreateClientInput['maritalStatus'],
    nic: client.nic ?? undefined,
    nationality: client.nationality ?? undefined,
    religion: client.religion ?? undefined,
    phone: client.phone,
    whatsapp: client.whatsapp ?? undefined,
    email: client.email ?? undefined,
    address: client.address ?? undefined,
    city: client.city ?? undefined,
    district: (client.district ?? undefined) as CreateClientInput['district'],
    emergencyName: client.emergencyName ?? undefined,
    emergencyPhone: client.emergencyPhone ?? undefined,
    passportStatus: client.passportStatus as PassportStatus,
    passportNo: client.passportNo ?? undefined,
    passportIssued: client.passportIssued ?? undefined,
    passportExpiry: client.passportExpiry ?? undefined,
    educationLevel: (client.educationLevel ?? undefined) as CreateClientInput['educationLevel'],
    experienceYears: client.experienceYears ?? undefined,
    expectedSalary: client.expectedSalary ?? undefined,
    salaryCurrency: (client.salaryCurrency ?? undefined) as CreateClientInput['salaryCurrency'],
    availableFrom: client.availableFrom ?? undefined,
    heightCm: client.heightCm ?? undefined,
    weightKg: client.weightKg ?? undefined,
    preferredCountries: client.preferredCountries,
    jobCategoryIds: client.jobCategoryIds,
    languages: client.languages as CreateClientInput['languages'],
    foreignExperience: client.foreignExperience.map((f) => ({
      countryCode: f.countryCode,
      role: f.role,
      years: f.years,
    })),
    consentSms: client.consentSms,
    consentWhatsapp: client.consentWhatsapp,
    consentEmail: client.consentEmail,
  }
}

function EditDetails({ client, onDone }: { client: ClientDetail; onDone: () => void }) {
  const update = useUpdateClient(client.id)
  const [error, setError] = useState<string | null>(null)

  return (
    <ClientForm
      defaultValues={toFormValues(client)}
      submitLabel="Save changes"
      loading={update.isPending}
      excludeId={client.id}
      error={error}
      onCancel={onDone}
      onSubmit={(values) => {
        setError(null)
        update.mutate(values, {
          onSuccess: onDone,
          onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Could not save'),
        })
      }}
    />
  )
}

function NotesTab({ clientId, canWrite }: { clientId: string; canWrite: boolean }) {
  const { data, isLoading } = useQuery(clientNotesQuery(clientId))
  const addNote = useAddNote(clientId)
  const [body, setBody] = useState('')

  return (
    <div className="space-y-4">
      {canWrite && (
        <Card className="p-4">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a note — what was discussed, what happens next…"
            maxLength={2000}
          />
          <div className="mt-2 flex justify-end">
            <Button
              size="sm"
              disabled={!body.trim()}
              loading={addNote.isPending}
              onClick={() => addNote.mutate(body.trim(), { onSuccess: () => setBody('') })}
            >
              Add note
            </Button>
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      )}
      {data?.items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <FileText className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm text-[var(--color-muted-foreground)]">No notes yet.</p>
        </Card>
      )}

      <div className="space-y-2">
        {data?.items.map((note) => (
          <Card key={note.id} className="p-3">
            <p className="whitespace-pre-wrap text-sm">{note.body}</p>
            <p className="mt-2 text-xs text-[var(--color-muted-foreground)]">
              {note.userName ?? 'Unknown'} · {formatDateTime(note.createdAt)}
            </p>
          </Card>
        ))}
      </div>
    </div>
  )
}

const ACTION_LABELS: Record<string, string> = {
  'client.create': 'Client created',
  'client.update': 'Details updated',
  'client.delete': 'Client deleted',
  'client.restore': 'Client restored',
  'client.note_create': 'Note added',
  'client.bulk_update': 'Updated in a bulk action',
}

function RemindersTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery(clientRemindersQuery(clientId))
  const createReminder = useCreateReminder(clientId)
  const markDone = useMarkReminderDone(clientId)
  const deleteRem = useDeleteReminder(clientId)
  const [showForm, setShowForm] = useState(false)
  const [note, setNote] = useState('')
  const [remindOn, setRemindOn] = useState('')

  const reminders = data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          Add reminder
        </Button>
      </div>

      {showForm && (
        <Card className="p-4">
          <div className="space-y-3">
            <label className="block text-sm font-medium">
              Note
              <textarea
                className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-transparent p-2 text-sm"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What to be reminded about…"
              />
            </label>
            <label className="block text-sm font-medium">
              Remind on
              <input
                type="date"
                className="mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm"
                value={remindOn}
                onChange={(e) => setRemindOn(e.target.value)}
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={!note.trim() || !remindOn}
                loading={createReminder.isPending}
                onClick={() =>
                  createReminder.mutate(
                    { note: note.trim(), remindOn },
                    {
                      onSuccess: () => {
                        setNote('')
                        setRemindOn('')
                        setShowForm(false)
                      },
                    },
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {!isLoading && reminders.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Bell className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm text-[var(--color-muted-foreground)]">No reminders set.</p>
        </Card>
      )}

      <div className="space-y-2">
        {reminders.map((r) => (
          <Card key={r.id} className={cn('p-3', r.doneAt && 'opacity-50')}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={`text-sm ${r.doneAt ? 'line-through' : ''}`}>{r.note}</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  Due: {formatDateTime(r.remindOn)}
                  {r.doneAt && ` · Done: ${formatDateTime(r.doneAt)}`}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                {!r.doneAt && (
                  <Button variant="ghost" size="sm" onClick={() => markDone.mutate(r.id)}>
                    Done
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => deleteRem.mutate(r.id)}>
                  Delete
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  )
}

const MESSAGE_STATUS_TONE: Record<string, BadgeVariant> = {
  sent: 'success',
  queued: 'muted',
  sending: 'muted',
  failed: 'destructive',
  dead: 'destructive',
  skipped: 'muted',
}

function MessagesTab({ client, canSend }: { client: ClientDetail; canSend: boolean }) {
  const { data, isLoading } = useQuery(clientMessagesQuery(client.id))
  const [composing, setComposing] = useState(false)

  // What the client can actually receive, so we don't offer a dead channel.
  const consented: MessageChannel[] = MESSAGE_CHANNELS.filter((ch) =>
    ch === 'sms'
      ? client.consentSms
      : ch === 'whatsapp'
        ? client.consentWhatsapp
        : client.consentEmail,
  )

  return (
    <div className="space-y-4">
      {canSend && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {consented.length === 0
              ? 'This client has not consented to any channel.'
              : `Consents to: ${consented.map((c) => MESSAGE_CHANNEL_LABELS[c]).join(', ')}`}
          </p>
          <Button size="sm" disabled={consented.length === 0} onClick={() => setComposing(true)}>
            <Send className="h-3.5 w-3.5" />
            Send message
          </Button>
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      )}
      {data?.items.length === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <MessageSquare className="h-8 w-8 text-[var(--color-muted-foreground)]" />
          <p className="text-sm text-[var(--color-muted-foreground)]">No messages sent yet.</p>
        </Card>
      )}

      <div className="space-y-2">
        {data?.items.map((message) => (
          <MessageRow key={message.id} message={message} />
        ))}
      </div>

      {composing && (
        <ManualSendDialog
          client={client}
          channels={consented}
          onClose={() => setComposing(false)}
        />
      )}
    </div>
  )
}

function MessageRow({ message }: { message: ClientMessage }) {
  return (
    <Card className="p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="muted">{MESSAGE_CHANNEL_LABELS[message.channel]}</Badge>
            <span className="text-xs text-[var(--color-muted-foreground)]">
              {MESSAGE_REF_TYPE_LABELS[message.refType as MessageRefType] ?? message.refType}
            </span>
          </div>
          {message.subject && <p className="mt-1 text-sm font-medium">{message.subject}</p>}
          <p className="mt-1 whitespace-pre-wrap text-sm">{message.body}</p>
          {message.lastError && message.status !== 'sent' && (
            <p className="mt-1 text-xs text-[var(--color-destructive)]">{message.lastError}</p>
          )}
        </div>
        <div className="shrink-0 text-right">
          <Badge variant={MESSAGE_STATUS_TONE[message.status] ?? 'muted'}>{message.status}</Badge>
          <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
            {message.sentAt ? formatDateTime(message.sentAt) : formatDateTime(message.createdAt)}
          </p>
        </div>
      </div>
    </Card>
  )
}

function ManualSendDialog({
  client,
  channels,
  onClose,
}: {
  client: ClientDetail
  channels: MessageChannel[]
  onClose: () => void
}) {
  const send = useManualSend(client.id)
  const templates = useQuery(templatesQuery)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ManualSendInput>({
    resolver: zodResolver(manualSendSchema),
    defaultValues: { channel: channels[0], body: '' },
  })

  const channel = watch('channel')
  const channelTemplates = templates.data?.items.filter((t) => t.channel === channel) ?? []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md p-6">
        <h2 className="mb-4 text-lg font-semibold">Send message to {client.fullName}</h2>

        <form
          onSubmit={handleSubmit((values) => {
            setError(null)
            send.mutate(values, {
              onSuccess: onClose,
              onError: (e) =>
                setError(e instanceof ApiClientError ? e.message : 'Could not queue the message'),
            })
          })}
          className="space-y-4"
          noValidate
        >
          {error && (
            <div role="alert" className="text-sm text-[var(--color-destructive)]">
              {error}
            </div>
          )}

          <label className="block text-sm font-medium">
            Channel
            <Select className="mt-1" {...register('channel')}>
              {channels.map((ch) => (
                <option key={ch} value={ch}>
                  {MESSAGE_CHANNEL_LABELS[ch]}
                </option>
              ))}
            </Select>
          </label>

          {channelTemplates.length > 0 && (
            <label className="block text-sm font-medium">
              Use a template
              <Select
                className="mt-1"
                onChange={(e) => {
                  const t = channelTemplates.find((x) => x.id === e.target.value)
                  if (t) setValue('body', t.body)
                }}
              >
                <option value="">— None —</option>
                {channelTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </label>
          )}

          {channel === 'email' && (
            <label className="block text-sm font-medium">
              Subject
              <Input className="mt-1" {...register('subject')} />
            </label>
          )}

          <label className="block text-sm font-medium">
            Message
            <Textarea className="mt-1" rows={4} {...register('body')} />
            {errors.body && (
              <span className="text-xs text-[var(--color-destructive)]">{errors.body.message}</span>
            )}
          </label>

          <p className="text-xs text-[var(--color-muted-foreground)]">
            Placeholders like {'{{first_name}}'} are filled in automatically.
          </p>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={send.isPending}>
              Send
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}

function ActivityTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery(clientActivityQuery(clientId))

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }
  if (!data?.items.length) {
    return (
      <Card className="flex flex-col items-center gap-3 p-12 text-center">
        <Activity className="h-8 w-8 text-[var(--color-muted-foreground)]" />
        <p className="text-sm text-[var(--color-muted-foreground)]">Nothing recorded yet.</p>
      </Card>
    )
  }

  return (
    <Card className="divide-y divide-[var(--color-border)]">
      {data.items.map((entry) => (
        <div key={entry.id} className="p-3">
          <p className="text-sm">
            <span className="font-medium">{ACTION_LABELS[entry.action] ?? entry.action}</span>
            {entry.userName && (
              <span className="text-[var(--color-muted-foreground)]"> by {entry.userName}</span>
            )}
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
            {formatDateTime(entry.createdAt)}
          </p>
          {entry.diff && Object.keys(entry.diff).length > 0 && (
            <pre className="mt-2 overflow-x-auto rounded bg-[var(--color-muted)] p-2 text-xs">
              {JSON.stringify(entry.diff, null, 2)}
            </pre>
          )}
        </div>
      ))}
    </Card>
  )
}

function DetailCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="p-4">
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      <dl className="space-y-2">{children}</dl>
    </Card>
  )
}

function Row({
  label,
  value,
  mono,
  capitalise,
}: {
  label: string
  value: string | number | null | undefined
  mono?: boolean
  capitalise?: boolean
}) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <dt className="shrink-0 text-[var(--color-muted-foreground)]">{label}</dt>
      <dd className={cn('text-right', mono && 'font-mono text-xs', capitalise && 'capitalize')}>
        {value === null || value === undefined || value === '' ? (
          <span className="text-[var(--color-muted-foreground)]">—</span>
        ) : (
          value
        )}
      </dd>
    </div>
  )
}
